import { useState } from 'react';
import type { TestWebhookResult } from '@/lib/feexpayWebhook';

interface EndpointCardProps {
  url: string;
  prettyUrl: string;
  onTest: () => Promise<TestWebhookResult>;
  testing: boolean;
  onRefresh: () => void;
}

function CopyField({ label, value, hint }: { label: string; value: string; hint?: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      /* presse-papiers indisponible */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div>
      <label className="block text-[11px] font-semibold text-foreground-600 mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
        <code className="flex-1 min-w-0 h-10 px-3 rounded-md border border-background-200/70 bg-background-100 text-[11px] text-foreground-800 font-mono flex items-center overflow-x-auto whitespace-nowrap">
          {value || '—'}
        </code>
        <button
          type="button"
          onClick={copy}
          disabled={!value}
          className="h-10 px-3 rounded-md bg-foreground-950 text-background-50 text-[11px] font-semibold whitespace-nowrap flex items-center gap-1.5 hover:bg-foreground-800 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
        >
          <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
          {copied ? 'Copié' : 'Copier'}
        </button>
      </div>
      {hint && <p className="text-[10px] text-foreground-400 mt-1.5 leading-relaxed">{hint}</p>}
    </div>
  );
}

export default function EndpointCard({ url, prettyUrl, onTest, testing, onRefresh }: EndpointCardProps) {
  const [result, setResult] = useState<TestWebhookResult | null>(null);

  const runTest = async () => {
    setResult(null);
    const res = await onTest();
    setResult(res);
  };

  return (
    <div className="rounded-lg border border-background-200/70 bg-background-50 p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
          <i className="ri-webhook-line text-lg"></i>
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-foreground-950">URL du webhook</h3>
          <p className="text-[11px] text-foreground-500 mt-0.5">
            À coller dans FeexPay → Webhooks → Ajouter un nouveau webhook.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <CopyField
          label="URL à utiliser (endpoint serveur)"
          value={url}
          hint="C’est l’adresse réellement joignable : elle reçoit le POST de FeexPay, journalise tout puis confirme le paiement."
        />
        <CopyField
          label="URL courte (alias)"
          value={prettyUrl}
          hint="Adresse de présentation uniquement : elle nécessite une règle de proxy (Cloudflare) vers l’endpoint serveur pour recevoir des POST. Tant que ce n’est pas fait, utilisez l’URL ci-dessus."
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={runTest}
          disabled={testing}
          className="h-9 px-4 rounded-full bg-primary-500 text-background-50 text-[11px] font-semibold whitespace-nowrap flex items-center gap-1.5 hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i className={testing ? 'ri-loader-4-line animate-spin' : 'ri-flask-line'}></i>
          {testing ? 'Envoi…' : 'Envoyer un webhook de test'}
        </button>
        <button
          type="button"
          onClick={onRefresh}
          className="h-9 px-3 rounded-full border border-background-200/70 text-[11px] font-medium text-foreground-600 whitespace-nowrap flex items-center gap-1.5 hover:bg-background-100 transition-colors cursor-pointer"
        >
          <i className="ri-refresh-line"></i>
          Rafraîchir
        </button>
        <span className="text-[10px] text-foreground-400">
          Le test est journalisé mais ne modifie aucune donnée.
        </span>
      </div>

      {result && (
        <div
          className={`mt-3 rounded-lg border px-3 py-2.5 flex items-start gap-2 ${
            result.ok ? 'border-green-200 bg-green-50/70' : 'border-red-200 bg-red-50/70'
          }`}
        >
          <i className={`mt-0.5 ${result.ok ? 'ri-checkbox-circle-line text-green-600' : 'ri-error-warning-line text-red-600'}`}></i>
          <div className="min-w-0">
            <p className={`text-[11px] font-semibold ${result.ok ? 'text-green-800' : 'text-red-700'}`}>
              {result.ok ? 'Webhook reçu' : 'Test échoué'}
            </p>
            <p className="text-[11px] text-foreground-600 mt-0.5 break-words">{result.message}</p>
            {result.ok && (
              <p className="text-[10px] text-foreground-400 mt-1">
                Événement <span className="font-mono">{result.event}</span>
                {result.logId ? ` — ligne de journal #${result.logId}` : ''}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}